import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "sonner";
import { ArrowLeft, CalendarDays, MapPin, Clock, GraduationCap, Tag, FileText, Download } from "lucide-react";
import { resolveUrl } from "@/lib/media";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { Analytics } from "@/components/site/Analytics";
import { Grain } from "@/components/site/Grain";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { MediaView, mediaWrapClass } from "@/components/site/MediaView";
import { SmartGallery } from "@/components/site/SmartGallery";
import { JoinDialog } from "@/components/site/JoinDialog";
import { MapsWaze } from "@/components/site/MapsWaze";
import { ActionButtons } from "@/components/site/ActionButtons";
import { formationRemaining, sessionRemaining } from "@/components/site/Formations";
import { isLocked, isPast, pastBadgeLabel } from "@/lib/publish";

const FormationBody = () => {
  const { content } = useContent();
  const { slug } = useParams();
  const fm = content.formations || {};
  const items = fm.items || [];
  const item = items.find((it) => (it.slug === slug || it.id === slug) && (it.status || "published") !== "hidden");
  const [open, setOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const rem = item ? formationRemaining(item) : 0;
  const isFull = item && (item.sessions || []).length > 0 && rem === 0;
  const locked = item ? isLocked(item) : false;
  const past = item ? isPast(item) : false;
  const closedAction = locked || past;
  const nextOpen = item ? (item.sessions || []).find((s) => sessionRemaining(s) > 0) : null;
  const facts = item ? [
    item.level && { icon: GraduationCap, label: item.level },
    item.duration && { icon: Clock, label: item.duration },
    item.price && { icon: Tag, label: item.price },
  ].filter(Boolean) : [];

  return (
    <div className="App bg-[#050505] min-h-screen flex flex-col">
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <Navbar />
      <main className="flex-1 pt-32 md:pt-40 pb-24">
        {!item ? (
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
            <div className="text-[11px] tracking-[0.35em] uppercase text-electric mb-4">Erreur 404</div>
            <h1 className="font-serif text-5xl md:text-7xl text-white mb-8">Formation introuvable</h1>
            <Link to="/formations" className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white rounded-full text-[13px] tracking-[0.12em] uppercase hover:border-electric hover:text-electric transition-colors"><ArrowLeft size={15} /> Toutes les formations</Link>
          </div>
        ) : (
          <article className="max-w-5xl mx-auto px-6 md:px-12" data-testid="formation-detail">
            <Reveal>
              <Link to="/formations" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-secondary2 hover:text-electric transition-colors mb-8"><ArrowLeft size={14} /> Formations</Link>
              <div className="text-[11px] tracking-[0.3em] uppercase text-electric mb-4">{fm.detailEyebrow || "Formation"}</div>
              <h1 data-testid="formation-detail-title" className="font-serif text-4xl md:text-7xl text-white tracking-tight leading-[0.98] mb-6">{item.title}</h1>
              {facts.length > 0 && (
                <div className="flex flex-wrap gap-3 mb-8">
                  {facts.map((f, i) => <span key={i} className="inline-flex items-center gap-2 text-sm text-white/85 border border-white/10 rounded-full px-4 py-1.5"><f.icon size={14} className="text-electric" /> {f.label}</span>)}
                </div>
              )}
            </Reveal>

            <Reveal className="mb-14">
              <div className={mediaWrapClass(item.detailFrame).cls}><MediaView item={item} alt={item.title} frame={item.detailFrame} /></div>
            </Reveal>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-12">
              <div>
                <Reveal className="mb-12">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-3">{fm.aboutLabel || "Présentation"}</div>
                  <p className="text-lg text-white/90 font-light leading-relaxed mb-4">{item.description}</p>
                  {item.presentation && <p className="text-base md:text-lg text-secondary2 font-light leading-relaxed whitespace-pre-line">{item.presentation}</p>}
                </Reveal>

                {(() => {
                  const programs = (item.programs && item.programs.length) ? item.programs
                    : ((item.program && item.program.length) ? [{ id: "legacy", title: fm.programLabel || "Programme", steps: item.program }] : []);
                  if (!programs.length) return null;
                  return (
                    <Reveal className="mb-12" data-testid="formation-programs">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-4">{fm.programLabel || "Programme"}</div>
                      <div className="space-y-8">
                        {programs.map((prog, pi) => (
                          <div key={prog.id || pi} data-testid={`formation-program-${pi}`}>
                            {prog.title && programs.length > 1 && <div className="font-serif text-2xl text-white mb-4">{prog.title}</div>}
                            <ol className="relative border-l border-white/10 ml-2">
                              {(prog.steps || []).map((p, i) => (
                                <li key={p.id || i} className="ml-6 pb-7 last:pb-0">
                                  <span className="absolute -left-[7px] mt-1.5 h-3.5 w-3.5 rounded-full bg-electric border-2 border-[#050505]" />
                                  {p.time && <div className="text-xs tracking-[0.15em] uppercase text-electric mb-1">{p.time}</div>}
                                  <div className="font-serif text-xl text-white">{p.title}</div>
                                  {p.text && <p className="text-sm text-secondary2 font-light mt-1">{p.text}</p>}
                                </li>
                              ))}
                            </ol>
                          </div>
                        ))}
                      </div>
                    </Reveal>
                  );
                })()}

                {(item.documents || []).filter((d) => d.url).length > 0 && (
                  <Reveal className="mb-12" data-testid="formation-documents">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-4">Documents & ressources</div>
                    <div className="space-y-2.5">
                      {item.documents.filter((d) => d.url).map((d, i) => (
                        <a key={d.id || i} href={resolveUrl(d.url)} target="_blank" rel="noreferrer" download data-testid={`formation-doc-${i}`}
                          className="group flex items-center justify-between gap-4 rounded-lg border border-white/10 bg-[#0a0a0a] px-4 py-3.5 hover:border-electric transition-colors">
                          <span className="flex items-center gap-3 min-w-0">
                            <FileText size={18} className="text-electric shrink-0" />
                            <span className="min-w-0">
                              <span className="block text-sm text-white truncate">{d.name || d.filename || "Document"}</span>
                              {d.description && <span className="block text-xs text-secondary2 truncate">{d.description}</span>}
                            </span>
                          </span>
                          <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.1em] uppercase text-secondary2 group-hover:text-electric shrink-0"><Download size={15} /> Télécharger</span>
                        </a>
                      ))}
                    </div>
                  </Reveal>
                )}

                {(item.gallery || []).length > 0 && <Reveal className="mb-4"><SmartGallery images={item.gallery} options={item.gallerySettings} mode={item.gallerySettings ? undefined : "slide"} label="Galerie" /></Reveal>}
              </div>

              <Reveal delay={0.1}>
                <div className="rounded-xl border border-white/10 bg-surface/50 p-6 lg:sticky lg:top-28">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-4">{fm.sessionsLabel || "Prochaines sessions"}</div>
                  {(item.sessions || []).length === 0 ? (
                    <p className="text-sm text-secondary2 mb-5">Dates bientôt annoncées.</p>
                  ) : (
                    <div className="space-y-3 mb-6">
                      {item.sessions.map((s, si) => {
                        const r = sessionRemaining(s);
                        return (
                          <div key={s.id || si} className={`rounded-lg border p-3 ${r === 0 ? "border-white/10 opacity-70" : "border-electric/30"}`} data-testid={`formation-session-${si}`}>
                            <div className="flex items-center gap-2 text-sm text-white/90"><CalendarDays size={14} className="text-electric" /> {s.date}{s.time ? ` · ${s.time}` : ""}</div>
                            {s.address
                              ? <div className="mt-1.5"><MapsWaze name={s.location} address={s.address} testid={`formation-session-maps-${si}`} /></div>
                              : s.location && <div className="flex items-center gap-2 text-xs text-secondary2 mt-1.5"><MapPin size={13} /> {s.location}</div>}
                            <div className={`text-xs mt-2 ${r === 0 ? "text-red-400" : "text-electric"}`}>{r === 0 ? (fm.fullLabel || "Complet") : `${r} ${fm.spotsSuffix || "places restantes"}`}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {(item.sessions || []).length > 0 && (
                    nextOpen
                      ? <div className="mb-5 text-xs text-electric" data-testid="formation-next-session">● Prochaine session disponible : <span className="text-white">{nextOpen.date}{nextOpen.time ? ` · ${nextOpen.time}` : ""}</span></div>
                      : <div className="mb-5 text-xs text-red-400" data-testid="formation-all-full">Toutes les sessions sont complètes — nouvelles dates à venir.</div>
                  )}
                  <button data-testid="formation-subscribe-btn" disabled={isFull || closedAction} onClick={() => setOpen(true)}
                    className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-[12px] tracking-[0.12em] uppercase font-semibold transition-colors ${isFull || closedAction ? "bg-white/5 text-white/40 border border-white/10 cursor-not-allowed" : "bg-electric text-black hover:bg-white"}`}>
                    {past ? "Terminé" : locked ? (fm.comingLabel || "Bientôt disponible") : isFull ? (fm.fullLabel || "Complet") : (fm.ctaLabel || "S'inscrire")}
                  </button>
                  <button data-testid="formation-question-btn" onClick={() => setInfoOpen(true)}
                    className="w-full mt-2.5 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white/80 text-[12px] tracking-[0.12em] uppercase hover:border-electric hover:text-electric transition-colors">
                    Poser une question
                  </button>
                  {locked && <p className="text-[11px] text-amber-400/90 mt-2 text-center" data-testid="formation-coming-note">Inscription pas encore ouverte.</p>}
                  {past && <p className="text-[11px] text-purple-300/90 mt-2 text-center" data-testid="formation-past-note">Cette formation est terminée — les inscriptions sont closes.</p>}
                </div>
              </Reveal>
            </div>

            {(item.actions || []).length > 0 && (
              <Reveal className="mb-4"><ActionButtons actions={item.actions} context={{ refId: item.id, refTitle: item.title }} /></Reveal>
            )}
          </article>
        )}
      </main>
      <Footer />
      {item && <JoinDialog open={open} onClose={() => setOpen(false)} type="formation" refId={item.id} refTitle={item.title}
        sessionId={(item.sessions || [])[0]?.id || ""} sessionLabel={(item.sessions || [])[0] ? `${(item.sessions || [])[0].date || ""}${(item.sessions || [])[0].time ? " · " + (item.sessions || [])[0].time : ""}` : ""}
        questions={item.questions || []}
        roles={[]} heading={`S'inscrire — ${item.title}`} intro="Renseignez vos coordonnées pour déposer votre candidature. Votre place sera confirmée après validation." />}
      {item && <JoinDialog open={infoOpen} onClose={() => setInfoOpen(false)} type="demande_info" refId={item.id} refTitle={item.title}
        sessionId={(item.sessions || [])[0]?.id || ""} sessionLabel={(item.sessions || [])[0] ? `${(item.sessions || [])[0].date || ""}${(item.sessions || [])[0].time ? " · " + (item.sessions || [])[0].time : ""}` : ""}
        heading={`Une question sur — ${item.title}`} intro="Posez votre question, nous vous répondrons. Aucune inscription n'est créée." />}
    </div>
  );
};

export default function FormationDetail() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <Analytics />
        <FormationBody />
      </ReactLenis>
    </ContentProvider>
  );
}
